/**
 * Predictive Anomaly Detection
 * Shows predicted issues 24-48 hrs ahead with root cause attribution.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Clock, TrendingDown, MapPin, Users, Thermometer, ChevronDown, ShieldAlert, Zap, Calendar } from 'lucide-react';
import { getIndustryTerms } from '@/config/industryTerminology';

// Neutral, terminology-adapted predictions for every industry without a
// bespoke dataset — so a real client never sees hospitality "dinner over
// capacity" style alerts. Universally relevant to any business.
const buildGenericPredictions = (t) => ([
  {
    id: 1, severity: 'high', timeframe: '48 hrs', title: 'Cash position projected to tighten next week',
    confidence: 80,
    rootCauses: [
      { factor: 'Several large supplier payments fall in the same week', weight: 55, icon: TrendingDown },
      { factor: `${t.customers} with overdue invoices not yet chased`, weight: 45, icon: Users },
    ],
    recommendation: 'Bring forward invoicing on completed work and chase overdue accounts now. Consider staggering non-urgent supplier payments to smooth the week.',
    projectedImpact: 'Tight cash buffer mid-week',
    mitigatedImpact: 'Comfortable buffer maintained with faster collections',
  },
  {
    id: 2, severity: 'medium', timeframe: '48 hrs', title: `${t.customer} churn risk rising among inactive accounts`,
    confidence: 76,
    rootCauses: [
      { factor: `${t.customers} with no activity in 30+ days`, weight: 60, icon: Users },
      { factor: 'No proactive follow-up scheduled', weight: 40, icon: ShieldAlert },
    ],
    recommendation: `Reach out to inactive ${t.customersLower} this week with a personal check-in or offer. Early outreach recovers most at-risk relationships.`,
    projectedImpact: `Revenue from at-risk ${t.customersLower} exposed`,
    mitigatedImpact: 'Most relationships retained with timely outreach',
  },
]);

const QSR_PREDICTIONS = [
  {
    id: 1,
    severity: 'high',
    timeframe: '24 hrs',
    title: 'Penrith Friday revenue projected 18% below normal',
    location: 'Penrith',
    confidence: 82,
    rootCauses: [
      { factor: '2 experienced staff on leave (Sarah M. + James K.)', weight: 55, icon: Users },
      { factor: 'Local school holiday ends — reduced family traffic expected', weight: 30, icon: Calendar },
      { factor: 'Competitor (Oporto) running 20% off promo within 2km', weight: 15, icon: ShieldAlert },
    ],
    recommendation: 'Reduce prep volume by 15%, shift marketing to delivery channel. Call in Jake T. (available casual) for the lunch rush to cover the cross-training gap.',
    projectedImpact: '-$2,250 revenue if unaddressed',
    mitigatedImpact: '-$800 revenue with recommended actions',
  },
  {
    id: 2,
    severity: 'medium',
    timeframe: '48 hrs',
    title: 'Footscray labour cost trending to 28.5% by Sunday',
    location: 'Footscray',
    confidence: 76,
    rootCauses: [
      { factor: 'Weekend roster has 3 overtime shifts (penalty rates apply)', weight: 60, icon: Users },
      { factor: 'Weekend revenue typically 12% below weekday at this location', weight: 40, icon: TrendingDown },
    ],
    recommendation: 'Swap 2 overtime shifts to regular casuals (saving $420/weekend). Consider reducing Sunday close team from 4 to 3 — Footscray Sunday evenings average only 35 transactions after 7pm.',
    projectedImpact: 'Labour cost 28.5% vs 25% target',
    mitigatedImpact: 'Labour cost ~26.1% with roster adjustment',
  },
  {
    id: 3,
    severity: 'low',
    timeframe: '48 hrs',
    title: 'Prospect food cost spike — chicken supplier delivery delayed',
    location: 'Prospect',
    confidence: 71,
    rootCauses: [
      { factor: 'Supplier (Ingham\'s) flagged 24-hr delivery delay for SA region', weight: 70, icon: Thermometer },
      { factor: 'Current chicken stock covers ~1.5 days at normal volume', weight: 30, icon: AlertTriangle },
    ],
    recommendation: 'Pre-order emergency stock from backup supplier (Baiada) at +8% cost. Alternatively, push fish burger combo as the feature item for Saturday to reduce chicken dependency.',
    projectedImpact: 'Potential stockout Saturday afternoon',
    mitigatedImpact: 'Full coverage with backup supplier order',
  },
];

const GENERIC_PREDICTIONS = {
  technology: [
    {
      id: 1, severity: 'high', timeframe: '24 hrs', title: 'API response time degrading — P95 projected to exceed 2s SLA by tomorrow',
      confidence: 85,
      rootCauses: [
        { factor: 'Database connection pool exhaustion during peak hours', weight: 65, icon: Zap },
        { factor: '3 enterprise customers running bulk exports simultaneously', weight: 35, icon: Users },
      ],
      recommendation: 'Increase connection pool from 50 to 100. Implement rate limiting on bulk export endpoint. Notify enterprise customers of maintenance window.',
      projectedImpact: 'SLA breach affecting 12 enterprise accounts',
      mitigatedImpact: 'P95 stays under 1.5s with pool increase',
    },
    {
      id: 2, severity: 'medium', timeframe: '48 hrs', title: '2 enterprise accounts at high churn risk (combined $8,400 MRR)',
      confidence: 78,
      rootCauses: [
        { factor: 'No login activity for 14+ days', weight: 50, icon: Users },
        { factor: 'Unresolved support tickets (API rate limits)', weight: 50, icon: ShieldAlert },
      ],
      recommendation: 'Schedule proactive CS calls within 48 hours. Offer temporary rate limit increase. Prepare migration to Enterprise Plus plan.',
      projectedImpact: '$8,400 MRR at risk',
      mitigatedImpact: '$0 churn with proactive outreach (89% save rate historically)',
    },
  ],
  hospitality: [
    {
      id: 1, severity: 'high', timeframe: '24 hrs', title: 'Saturday dinner projected 20% over capacity — 12 reservations above usual',
      confidence: 88,
      rootCauses: [
        { factor: 'Local event (wine festival) driving foot traffic', weight: 60, icon: Calendar },
        { factor: 'New Google Ads campaign driving 40% more reservation clicks', weight: 40, icon: Zap },
      ],
      recommendation: 'Add 2 servers and 1 kitchen hand to Saturday roster. Pre-prep signature dishes. Set up overflow seating in the courtyard.',
      projectedImpact: '30+ minute wait times, 15% walkout rate',
      mitigatedImpact: 'Under 10 min wait, capture ~$3,200 in additional covers',
    },
  ],
};

const severityConfig = {
  high: { color: 'bg-red-500', border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-100 text-red-700 border-red-200' },
  medium: { color: 'bg-amber-500', border: 'border-amber-200', bg: 'bg-amber-50', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
  low: { color: 'bg-blue-500', border: 'border-blue-200', bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700 border-blue-200' },
};

export const PredictiveAnomalyDetection = ({ industry = 'general' }) => {
  const [expandedId, setExpandedId] = useState(null);
  const t = getIndustryTerms(industry);
  // hospitality/qsr keep the rich venue dataset; technology keeps its SaaS
  // dataset; every other industry gets the neutral terminology-adapted set.
  const predictions = t.key === 'hospitality' ? QSR_PREDICTIONS
    : GENERIC_PREDICTIONS[t.key] || buildGenericPredictions(t);

  return (
    <Card className="glass-card overflow-hidden" data-testid="predictive-anomaly-detection">
      <div className="h-1 bg-gradient-to-r from-red-500 via-amber-500 to-emerald-500" />
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-amber-500" />
          Predictive Alerts
          <Badge variant="outline" className="text-[10px] ml-1">Next 48 hrs</Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">Issues predicted before they happen — with root cause and mitigation</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {predictions.map(pred => {
          const cfg = severityConfig[pred.severity];
          const isExpanded = expandedId === pred.id;
          return (
            <div
              key={pred.id}
              className={`border ${cfg.border} rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-sm`}
              onClick={() => setExpandedId(isExpanded ? null : pred.id)}
              data-testid={`anomaly-${pred.id}`}
            >
              <div className={`flex items-start gap-3 p-3 ${cfg.bg}`}>
                <div className={`w-2 h-2 rounded-full ${cfg.color} mt-1.5 shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800">{pred.title}</span>
                    <Badge className={`text-[10px] ${cfg.badge}`}>{pred.severity}</Badge>
                    <Badge variant="outline" className="text-[10px]">
                      <Clock className="w-3 h-3 mr-0.5" />{pred.timeframe}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">{pred.confidence}% confident</Badge>
                  </div>
                  {pred.location && (
                    <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />{pred.location}
                    </p>
                  )}
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </div>

              {isExpanded && (
                <div className="px-3 pb-3 space-y-3 border-t border-slate-100" data-testid={`anomaly-detail-${pred.id}`}>
                  {/* Root Causes */}
                  <div className="pt-3">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Root Cause Analysis</p>
                    <div className="space-y-2">
                      {pred.rootCauses.map((cause, i) => {
                        const Icon = cause.icon;
                        return (
                          <div key={i} className="flex items-center gap-2">
                            <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-slate-700">{cause.factor}</p>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${cfg.color}`} style={{ width: `${cause.weight}%` }} />
                              </div>
                              <span className="text-[10px] text-slate-500 w-8 text-right">{cause.weight}%</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Impact comparison */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="bg-red-50 rounded-lg p-2.5 border border-red-100">
                      <p className="text-[10px] uppercase text-red-500 font-semibold mb-0.5">If Unaddressed</p>
                      <p className="text-xs font-semibold text-red-700">{pred.projectedImpact}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-2.5 border border-emerald-100">
                      <p className="text-[10px] uppercase text-emerald-500 font-semibold mb-0.5">With Mitigation</p>
                      <p className="text-xs font-semibold text-emerald-700">{pred.mitigatedImpact}</p>
                    </div>
                  </div>

                  {/* Recommendation */}
                  <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Recommended Action</p>
                    <p className="text-xs text-slate-700 leading-relaxed">{pred.recommendation}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default PredictiveAnomalyDetection;
